import React, { useEffect } from 'react';
import { useFormContext, useFieldArray } from 'react-hook-form';
import { Box, Button, Grid } from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers-pro';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import AddIcon from '@mui/icons-material/Add';
import useReactHookFormStyles from '../../../../../custom-components/form-components/ReactHookForm.styles';
import DateFieldInput, { convertMinutesToTime } from '../../field-array/DateField';

const Windows = ({ name, buttonLabel, show }) => {
    const { fieldArray } = useReactHookFormStyles();
    const { control, watch, setValue } = useFormContext();

    const { fields, append, remove } = useFieldArray({
        control,
        name,
    });

    const allStarts = watch(fields.map((_, index) => `${name}[${index}].start`));
    const allEnds = watch(fields.map((_, index) => `${name}[${index}].end`));

    useEffect(() => {
        fields.forEach((_, index) => {
            const start = allStarts[index];
            const end = allEnds[index];
            const previousEnd = index > 0 ? allEnds[index - 1] : null;

            if (index === 0 && start === null && end === null) {
                setValue(`${name}[0].start`, 0); // 12:00 AM
                setValue(`${name}[0].end`, 1439); // 11:59 PM
                return;
            }

            // ensure end is automatically always after start
            if (start != null) {
                if (start != null && (!end || end < start)) {
                    setValue(`${name}[${index}].end`, start + 1);
                }
            }

            // ensure start is after the end of previous index
            if (previousEnd != null && start <= previousEnd) {
                setValue(`${name}[${index}].start`, previousEnd + 1);
            }
        });
    }, [allStarts, allEnds, fields]);

    return (
        <Box sx={{ display: show ? undefined : 'none' }}>
            {fields.map((field, index) => {
                const previousEnd = watch(`${name}[${index - 1}].end`);
                const minStart = convertMinutesToTime(previousEnd);
                const start = watch(`${name}[${index}].start`);
                const minEnd = convertMinutesToTime(start);
                return (
                    <Grid
                        key={`${field.name}.${field.id}`}
                        container
                        className={fieldArray}
                        spacing={2}
                        // wrap
                    >
                        <Grid item xs={11}>
                            <Grid container spacing={1} justifyContent="flex-start">
                                <Grid item>
                                    <DateFieldInput
                                        Picker={TimePicker}
                                        key={`start.${field.id}`}
                                        name={`${name}[${index}].start`}
                                        label="Start Time"
                                        InputLabelProps={{ shrink: true }}
                                        slotProps={{
                                            textField: {
                                                InputLabelProps: { shrink: true },
                                                required: true,
                                            },
                                        }}
                                        control={control}
                                        views={['hours', 'minutes']}
                                        minTime={minStart}
                                        // wrap
                                    />
                                </Grid>
                                <Grid item>
                                    <DateFieldInput
                                        Picker={TimePicker}
                                        key={`end.${field.id}`}
                                        name={`${name}[${index}].end`}
                                        label="End Time"
                                        slotProps={{
                                            textField: {
                                                InputLabelProps: { shrink: true },
                                                required: true,
                                            },
                                        }}
                                        control={control}
                                        views={['hours', 'minutes']}
                                        minTime={minEnd}
                                        // wrap
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item xs={1} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Button onClick={() => remove(index)}>
                                <HighlightOffIcon style={{ color: 'red' }} />
                            </Button>
                        </Grid>
                    </Grid>
                );
            })}
            <Button variant="outlined" onClick={() => append({ start: null, end: null })}>
                <AddIcon sx={{ marginRight: 1 }} /> {buttonLabel}
            </Button>
        </Box>
    );
};

export default Windows;
